import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectAuthPost } from './connectApi';
import './styles/NotificationsBell.css';

const POLL_INTERVAL = 15 * 1000;

/**
 * Notification bell with unread badge + dropdown.
 * Polls the cheap unread-count endpoint every 15s (paused while the tab is
 * hidden); the full list loads only when the dropdown opens, which also
 * marks everything read.
 */
export default function NotificationsBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const wrapperRef = useRef(null);

  const pollCount = useCallback(async () => {
    try {
      const result = await connectAuthPost('connect/notifications/unread-count');
      if (result.success) {
        setUnread(result.data.unread);
      }
    } catch (err) {
      // Silent — bell is non-critical
    }
  }, []);

  const loadList = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const result = await connectAuthPost('connect/notifications/list');
      if (result.success) {
        setNotifications(result.data.notifications);
        setUnread(result.data.unread);
      }
    } catch (err) {
      // Silent — bell is non-critical
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    pollCount();
    const interval = setInterval(() => {
      if (!document.hidden) pollCount();
    }, POLL_INTERVAL);

    // Immediate refresh when the user returns to the tab
    const onVisible = () => {
      if (!document.hidden) pollCount();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollCount]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleToggle = async () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (!opening) return;

    // Fresh list on every open; rows keep their unread highlight until next open
    await loadList();
    setUnread(0);
    try {
      await connectAuthPost('connect/notifications/mark-read');
    } catch (err) {
      // Non-critical
    }
  };

  const handleItemClick = (notif) => {
    setIsOpen(false);
    if (notif.type === 'follow') {
      // See who followed you
      navigate('/connect/user/' + notif.actor_id);
    } else if (notif.type === 'message') {
      // Open the chat with the sender
      navigate('/connect/messages?user=' + notif.actor_id);
    } else {
      // Like/comment happened on your own post
      navigate('/connect/profile');
    }
  };

  const describe = (notif) => {
    switch (notif.type) {
      case 'like': return 'liked your post';
      case 'comment': return 'commented on your post';
      case 'follow': return 'started following you';
      case 'message': return 'sent you a message';
      default: return 'interacted with you';
    }
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - timestamp * 1000;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  return (
    <div className="connect-notif-wrapper" ref={wrapperRef}>
      <button className="connect-notif-bell" onClick={handleToggle} title="Notifications">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span className="connect-notif-badge">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {isOpen && (
        <div className="connect-notif-dropdown">
          <div className="connect-notif-header">Notifications</div>
          {isLoadingList ? (
            <p className="connect-notif-empty">Loading...</p>
          ) : notifications.length === 0 ? (
            <p className="connect-notif-empty">Nothing yet — go connect with people!</p>
          ) : (
            <div className="connect-notif-list">
              {notifications.map(notif => (
                <button
                  key={notif.id}
                  className={`connect-notif-item ${notif.is_read ? '' : 'unread'}`}
                  onClick={() => handleItemClick(notif)}
                >
                  <div className="connect-notif-avatar">
                    {notif.actor_picture ? (
                      <img src={notif.actor_picture} alt={notif.actor_name} />
                    ) : (
                      <span>{notif.actor_name ? notif.actor_name.charAt(0).toUpperCase() : '?'}</span>
                    )}
                  </div>
                  <div className="connect-notif-text">
                    <span><strong>{notif.actor_name}</strong> {describe(notif)}</span>
                    <span className="connect-notif-time">{formatTime(notif.timestamp)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
